import * as i2 from '@angular/common';
import { AsyncPipe, CommonModule, DOCUMENT } from '@angular/common';
import * as i1 from '@angular/common/http';
import { HttpClientModule, HttpErrorResponse } from '@angular/common/http';
import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, inject, ChangeDetectorRef, Pipe, EventEmitter, Component, ChangeDetectionStrategy, Output, ViewChild, DestroyRef, Directive, Input, NgModule } from '@angular/core';
import * as i9 from '@pure/hive';
import { TooltipDirective, LoadingSpinnerComponent, PopUpComponent, TooltipModule, ButtonComponent, PopUpTriggerDirective, OrganizationSwitcherComponent, SelectionMenuModule, ContentDialogComponent, DropdownModule, AutofocusDirective, AvatarMenuComponent, AvatarModule, IconComponent, RootDirective, LabelComponent, TagComponent, PopUpModule, HiveTemplateDirective } from '@pure/hive';
import { Subject, repeat, shareReplay, ReplaySubject, of, withLatestFrom, switchMap, take, forkJoin, finalize, catchError, takeUntil, from, tap, map, BehaviorSubject, firstValueFrom, distinctUntilChanged, combineLatest, lastValueFrom } from 'rxjs';
import { smartTimer } from '@pstg/smart-timer';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as i10 from '@angular/router';
import { RouterModule } from '@angular/router';
import * as i5 from '@pure/authz-authorizer';
import { AuthorizerModule } from '@pure/authz-authorizer';
import * as i4 from '@angular/forms';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';

const AMPLITUDE_TRACK_CALLBACK = new InjectionToken('AMPLITUDE_TRACK_CALLBACK');

class GenericAmplitudeEvent {
    constructor(event_type, event_properties) {
        this.event_type = event_type;
        this.event_properties = event_properties;
    }
}
/**
 * Service for tracking events with Amplitude. Needs to have amplitude enabled in the platform configuration
 * and the Amplitude itself must be initialised in order for tracking to work.
 */
class AmplitudeService {
    constructor(amplitudeTrackCallback) {
        this.amplitudeTrackCallback = amplitudeTrackCallback;
    }
    /**
     * Tries to track amplitude event. Does nothing if Amplitude callback has not been set at module import.
     * @param event Amplitude event to track
     * @param options Event options
     * @returns
     */
    tryTrackEvent(event, options) {
        this.amplitudeTrackCallback(event, options);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AmplitudeService, deps: [{ token: AMPLITUDE_TRACK_CALLBACK }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AmplitudeService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AmplitudeService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [AMPLITUDE_TRACK_CALLBACK]
                }] }] });

class ManageOrganisationsClicked {
    constructor() {
        this.event_type = 'manage organisations clicked';
    }
}
class OrganisationSwitcherExpanded {
    constructor(event_properties) {
        this.event_properties = event_properties;
        this.event_type = 'organisation switcher expanded';
        this.event_properties = event_properties;
    }
}
class OrganisationToSwitchSelected {
    constructor(event_properties) {
        this.event_properties = event_properties;
        this.event_type = 'organisation to switch selected';
        this.event_properties = event_properties;
    }
}
class AppSwitcherClicked {
    constructor(event_properties) {
        this.event_properties = event_properties;
        this.event_type = 'app switcher clicked';
        this.event_properties = event_properties;
    }
}

class ApplicationsService {
    static { this.URL = '/platform/applications'; }
    static { this.USER_URL = ApplicationsService.URL + '/user'; }
    constructor(http) {
        this.http = http;
    }
    getUserApps() {
        return this.http.get(ApplicationsService.USER_URL);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ApplicationsService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ApplicationsService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ApplicationsService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.HttpClient }] });

class CachedApplicationsService {
    constructor(applicationsService) {
        this.applicationsService = applicationsService;
        this.request$ = null;
        this.refresh$ = new Subject();
    }
    /** Returns an Observable of applciations that emits when the cache gets refreshed. See {@link refresh} */
    getUserApps() {
        if (!this.request$) {
            this.request$ = this.applicationsService
                .getUserApps()
                .pipe(repeat({ delay: () => this.refresh$ }), shareReplay(1));
        }
        return this.request$;
    }
    refresh() {
        this.refresh$.next();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: CachedApplicationsService, deps: [{ token: ApplicationsService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: CachedApplicationsService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: CachedApplicationsService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: ApplicationsService }] });

function platform() {
    return window.dxp;
}

class PlatformClientService {
    constructor() {
        this.isLoggedIn$ = new ReplaySubject(1);
        this.introspect$ = new ReplaySubject(1);
        this.onIntrospect = (introspect) => {
            this.introspect$.next(introspect);
        };
        this.onLoggedInChange = (isLoggedIn) => {
            this.isLoggedIn$.next(isLoggedIn);
        };
        platform().ready.then(() => this.init());
    }
    init() {
        platform().registerIsLoggedInCallback(this.onLoggedInChange);
        platform().registerIntrospectCallback(this.onIntrospect);
    }
    getIsLoggedIn() {
        return this.isLoggedIn$.asObservable();
    }
    getEffectiveOrgUid() {
        return platform().getEffectiveOrgUid();
    }
    // Currently this information is available right from the start of the app,
    // since it is fetched before app is initialized.
    // Later, this will change to speed-up app loading.
    // It means that the effective org will be queried asynchronously.
    // In order not to break the API, we should keep it an observable right from the start.
    /**
     * @returns effective organization the user is switched to
     */
    getEffectiveOrg() {
        return of(platform().getEffectiveOrg());
    }
    getIntrospect() {
        return this.introspect$.asObservable();
    }
    getApplicationId() {
        return platform().config.app || '';
    }
    impersonationSupported() {
        return Boolean(platform().config.allowImpersonation);
    }
    login(params) {
        platform().login(params);
    }
    logout() {
        platform().logout();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PlatformClientService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PlatformClientService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PlatformClientService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [] });

function createPure1Link(opts) {
    const url = new URL(opts.path || '', `https://${opts.locationURL}`);
    if (opts.orgUid) {
        const orgUidPath = `/app/orgs/${opts.orgUid}`;
        if (url.pathname.startsWith('/app')) {
            url.pathname = url.pathname.replace('/app', orgUidPath);
        }
        else {
            url.pathname = orgUidPath + url.pathname;
            if (url.pathname.endsWith('/')) {
                url.pathname = url.pathname.slice(0, -1);
            }
        }
    }
    url.searchParams.append('client_id', opts.audience);
    url.searchParams.append('redirect_uri', opts.locationURL);
    url.searchParams.append('login_hint', opts.loginHint);
    if (opts.impersonateEmail) {
        url.searchParams.append('impersonate', opts.impersonateEmail);
    }
    return url.toString();
}

class Pure1NavigationService {
    constructor() {
        this.applicationsService = inject(CachedApplicationsService);
        this.platformService = inject(PlatformClientService);
    }
    /**
     * Constructs a link to the provided application
     * @param appId Can be found in the https://github.com/pure-pure1/pure1-ui-platform-apps-config/tree/main/apps
     * @param pathAndQuery The path and optionally query to append to the application's URL. Leading slash is optional.
     * @param orgUid The organization UID to construct an org-aware URL. Make sure that the target application supports this!
     * @example link('<app-id>', '/test') -> '<app-url>/test'
     * @returns A completed Observable that emits:
     * - a URL string when the id is valid and the user is authorized for the app,
     * - `undefined` when the id is invalid or the user is not authorized for the app.
     * */
    link(appId, pathAndQuery, orgUid) {
        return this.applicationsService.getUserApps().pipe(withLatestFrom(this.platformService.getIntrospect()), switchMap(([apps, introspect]) => {
            const app = apps.find((app) => app.id === appId);
            if (!app) {
                return of(undefined);
            }
            const url = createPure1Link({
                audience: app.audience,
                locationURL: app.locationURL,
                loginHint: introspect.act?.email ?? introspect.email,
                impersonateEmail: introspect.act && introspect.email,
                path: pathAndQuery,
                orgUid,
            });
            return of(url);
        }), take(1));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: Pure1NavigationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: Pure1NavigationService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: Pure1NavigationService, decorators: [{
            type: Injectable
        }] });

class Pure1AppLinkPipe {
    constructor() {
        this.asyncPipe = new AsyncPipe(inject(ChangeDetectorRef));
        this.pure1NavigationService = inject(Pure1NavigationService);
    }
    ngOnDestroy() {
        this.asyncPipe.ngOnDestroy();
    }
    transform(appId, pathAndQuery, orgUid) {
        const link$ = this.pure1NavigationService.link(appId, pathAndQuery, orgUid);
        return this.asyncPipe.transform(link$);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: Pure1AppLinkPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: Pure1AppLinkPipe, isStandalone: true, name: "pure1AppLink", pure: false }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: Pure1AppLinkPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'pure1AppLink',
                    standalone: true,
                    pure: false,
                }]
        }] });

class AppRedirectUrlPipe {
    transform(params, userEmail, impersonateEmail, path) {
        return createPure1Link({
            audience: params.audience,
            locationURL: params.locationURL,
            loginHint: userEmail,
            impersonateEmail: impersonateEmail,
            path,
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AppRedirectUrlPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: AppRedirectUrlPipe, isStandalone: true, name: "appRedirectUrl" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AppRedirectUrlPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'appRedirectUrl',
                    standalone: true,
                }]
        }] });

class AppSwitcherContentsComponent {
    constructor() {
        this.visibilityChange = new EventEmitter();
        this.userEmail = '';
        this.impersonateEmail = '';
        this.apps = [];
        this.loading = true;
        this.currentAppId = '';
        this.POP_UP_OFFSET_Y = 12;
        this.TOOLTIP_SHOW_DELAY = 500;
        this.cachedApplicationsService = inject(CachedApplicationsService);
        this.platformService = inject(PlatformClientService);
        this.changeDetectionRef = inject(ChangeDetectorRef);
        this.amplitudeService = inject(AmplitudeService);
    }
    ngOnInit() {
        this.currentAppId = this.platformService.getApplicationId();
        forkJoin([
            this.platformService.getIntrospect().pipe(take(1)),
            this.cachedApplicationsService.getUserApps().pipe(take(1), finalize(() => {
                this.loading = false;
                this.changeDetectionRef.detectChanges();
            })),
        ]).subscribe(([introspect, apps]) => {
            if (introspect.act) {
                this.userEmail = introspect.act.email;
                this.impersonateEmail = introspect.email;
            }
            else {
                this.userEmail = introspect.email;
            }
            this.apps = apps;
            this.changeDetectionRef.detectChanges();
        });
    }
    onAppClick(app) {
        this.amplitudeService.tryTrackEvent(new AppSwitcherClicked({ appId: app.id, appName: app.title }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AppSwitcherContentsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: AppSwitcherContentsComponent, isStandalone: true, selector: "platform-app-switcher-contents", outputs: { visibilityChange: "visibilityChange" }, viewQueries: [{ propertyName: "cachedApplicationsService", first: true, predicate: TooltipDirective, descendants: true }], ngImport: i0, template: "<hive-loading-spinner *ngIf=\"loading; else appList\" label=\"Loading applications...\" />\n<ng-template #appList>\n    <nav>\n        <ul #appList class=\"app-list\">\n            <li *ngFor=\"let app of apps\">\n                <a\n                    class=\"app-link\"\n                    [class.app-link--selected]=\"app.id === currentAppId\"\n                    target=\"_blank\"\n                    [attr.href]=\"app | appRedirectUrl: userEmail : impersonateEmail\"\n                    (click)=\"onAppClick(app)\"\n                >\n                    <img class=\"app-logo\" [src]=\"app.image\" alt=\"Logo\" />\n                    <span>{{ app.title }}</span>\n                </a>\n            </li>\n        </ul>\n    </nav>\n</ng-template>\n", styles: [".app-list{box-sizing:border-box;display:grid;grid-template-columns:repeat(3,80px);padding:0;margin:0;gap:16px;list-style:none}.app-link{box-sizing:border-box;width:100%;display:inline-grid;grid-template-rows:40px 28px;grid-template-columns:1fr;gap:8px;padding:8px 20px;justify-content:center;text-align:center;font-size:11px;line-height:1.25;overflow:hidden;color:#363636;text-decoration:none}.app-logo{width:100%;max-height:100%;overflow:hidden;object-fit:contain}.app-link{outline-offset:-2px}.app-link:hover,.app-link:focus{background-color:#fff6f8}.app-link--selected,.app-link:active{background-color:#f8f8f8;font-weight:600}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "ngmodule", type: HttpClientModule }, { kind: "pipe", type: AppRedirectUrlPipe, name: "appRedirectUrl" }, { kind: "component", type: 
                // ui-next
                LoadingSpinnerComponent, selector: "hive-loading-spinner", inputs: ["displayMode", "label", "size"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AppSwitcherContentsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'platform-app-switcher-contents', standalone: true, imports: [
                        CommonModule,
                        HttpClientModule,
                        Pure1AppLinkPipe,
                        AppRedirectUrlPipe,
                        // ui-next
                        LoadingSpinnerComponent,
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<hive-loading-spinner *ngIf=\"loading; else appList\" label=\"Loading applications...\" />\n<ng-template #appList>\n    <nav>\n        <ul #appList class=\"app-list\">\n            <li *ngFor=\"let app of apps\">\n                <a\n                    class=\"app-link\"\n                    [class.app-link--selected]=\"app.id === currentAppId\"\n                    target=\"_blank\"\n                    [attr.href]=\"app | appRedirectUrl: userEmail : impersonateEmail\"\n                    (click)=\"onAppClick(app)\"\n                >\n                    <img class=\"app-logo\" [src]=\"app.image\" alt=\"Logo\" />\n                    <span>{{ app.title }}</span>\n                </a>\n            </li>\n        </ul>\n    </nav>\n</ng-template>\n", styles: [".app-list{box-sizing:border-box;display:grid;grid-template-columns:repeat(3,80px);padding:0;margin:0;gap:16px;list-style:none}.app-link{box-sizing:border-box;width:100%;display:inline-grid;grid-template-rows:40px 28px;grid-template-columns:1fr;gap:8px;padding:8px 20px;justify-content:center;text-align:center;font-size:11px;line-height:1.25;overflow:hidden;color:#363636;text-decoration:none}.app-logo{width:100%;max-height:100%;overflow:hidden;object-fit:contain}.app-link{outline-offset:-2px}.app-link:hover,.app-link:focus{background-color:#fff6f8}.app-link--selected,.app-link:active{background-color:#f8f8f8;font-weight:600}\n"] }]
        }], propDecorators: { visibilityChange: [{
                type: Output
            }], cachedApplicationsService: [{
                type: ViewChild,
                args: [TooltipDirective]
            }] } });

class AppSwitcherComponent {
    constructor() {
        this.visibilityChange = new EventEmitter();
        this.userEmail = '';
        this.apps = [];
        this.loading = false;
        this.POP_UP_OFFSET_Y = 12;
        this.TOOLTIP_SHOW_DELAY = 500;
    }
    show() {
        this.popup.showOverlay();
    }
    hide() {
        this.popup.hideOverlay();
    }
    toggle() {
        this.popup.toggleOverlay();
    }
    _onVisibilityChange($event) {
        this.tooltip.hide();
        this.visibilityChange.emit($event);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AppSwitcherComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: AppSwitcherComponent, isStandalone: true, selector: "platform-app-switcher", outputs: { visibilityChange: "visibilityChange" }, viewQueries: [{ propertyName: "tooltip", first: true, predicate: TooltipDirective, descendants: true }, { propertyName: "popup", first: true, predicate: PopUpComponent, descendants: true }], ngImport: i0, template: "<hive-button\n    id=\"pure1_apps_menu\"\n    icon=\"light/grid.svg\"\n    emphasis=\"tertiary\"\n    shouldToggle\n    accessibleName=\"Switch application\"\n    [hivePopUpTrigger]=\"popup\"\n    hiveTooltip\n    hiveTooltipDescription=\"Pure1 Apps\"\n    [showDelay]=\"TOOLTIP_SHOW_DELAY\"\n    position=\"bottom\"\n/>\n\n<hive-pop-up\n    #popup\n    position=\"bottomEnd\"\n    closeOnOutsideClick\n    animationType=\"slide\"\n    [offsetY]=\"POP_UP_OFFSET_Y\"\n    (visibilityChange)=\"_onVisibilityChange($event)\"\n>\n    <platform-app-switcher-contents></platform-app-switcher-contents>\n</hive-pop-up>\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "ngmodule", type: HttpClientModule }, { kind: "ngmodule", type: TooltipModule }, { kind: "directive", type: i9.TooltipDirective, selector: "[hiveTooltip]", inputs: ["hiveTooltipHeading", "hiveTooltipDescription", "hiveTooltipCustomContentTemplateRef", "hiveTooltipCustomContentContext", "disableTooltip", "hiveTooltipPosition", "hideDelay", "showDelay", "windowClass"], outputs: ["tooltipWindowHovered"] }, { kind: "component", type: 
                // ui-next
                ButtonComponent, selector: "hive-button", inputs: ["type", "menuButtonActive", "hasMenuButtonCaret", "label", "accessibleName", "icon", "iconPosition", "emphasis", "title", "size", "isLoading", "disabled", "rounded", "width"] }, { kind: "component", type: PopUpComponent, selector: "hive-pop-up", inputs: ["testId", "showCloseButton", "dark", "position", "offsetX", "offsetY", "closeOnOutsideClick", "closeOnBackdropClick", "hasBackdrop"], outputs: ["visibilityChange"], exportAs: ["hivePopUp"] }, { kind: "directive", type: PopUpTriggerDirective, selector: "[hivePopUpTrigger]", inputs: ["hivePopUpTrigger"] }, { kind: "component", type: AppSwitcherContentsComponent, selector: "platform-app-switcher-contents", outputs: ["visibilityChange"] }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: AppSwitcherComponent, decorators: [{
            type: Component,
            args: [{ selector: 'platform-app-switcher', standalone: true, imports: [
                        CommonModule,
                        HttpClientModule,
                        TooltipModule,
                        // ui-next
                        ButtonComponent,
                        PopUpComponent,
                        PopUpTriggerDirective,
                        AppSwitcherContentsComponent,
                    ], template: "<hive-button\n    id=\"pure1_apps_menu\"\n    icon=\"light/grid.svg\"\n    emphasis=\"tertiary\"\n    shouldToggle\n    accessibleName=\"Switch application\"\n    [hivePopUpTrigger]=\"popup\"\n    hiveTooltip\n    hiveTooltipDescription=\"Pure1 Apps\"\n    [showDelay]=\"TOOLTIP_SHOW_DELAY\"\n    position=\"bottom\"\n/>\n\n<hive-pop-up\n    #popup\n    position=\"bottomEnd\"\n    closeOnOutsideClick\n    animationType=\"slide\"\n    [offsetY]=\"POP_UP_OFFSET_Y\"\n    (visibilityChange)=\"_onVisibilityChange($event)\"\n>\n    <platform-app-switcher-contents></platform-app-switcher-contents>\n</hive-pop-up>\n" }]
        }], propDecorators: { visibilityChange: [{
                type: Output
            }], tooltip: [{
                type: ViewChild,
                args: [TooltipDirective]
            }], popup: [{
                type: ViewChild,
                args: [PopUpComponent]
            }] } });

const windowFactory = () => {
    const { defaultView } = inject(DOCUMENT);
    if (!defaultView) {
        throw new Error('Window is not available');
    }
    return defaultView;
};
const WINDOW = new InjectionToken('An abstraction over global window object', {
    providedIn: 'root',
    factory: windowFactory,
});

const INTERVAL = 5 * 60 * 1000;
/**
 * This service fetches the current user from Phonebook every 5 minutes.
 * Fetching is done lazily, meaning that only after you call the getCurrentUser() for the first time, it will start fetching the current user.
 */
class CachedCurrentUserService {
    constructor(http, platformClient, window) {
        this.http = http;
        this.platformClient = platformClient;
        this.window = window;
        this.currentUser$ = new ReplaySubject(1);
        this.destroy$ = new Subject();
        this.initialised = false;
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    /**
     * Forces a new fetch of the current user.
     */
    forceRefresh() {
        this.destroy$.next();
        this.startFetching();
    }
    /**
     * Returns an observable that emits the current user every in regular intervals.
     * @returns An observable that emits every current user value this service gets from Phonebook.
     */
    getCurrentUser() {
        if (!this.initialised) {
            this.initialised = true;
            this.startFetching();
        }
        return this.currentUser$.asObservable();
    }
    /**
     * Retrieves the current user from Phonebook.
     * If the current user is not found, it will log out the user.
     * If the current user is impersonated, it will log in as the acting user.
     * @return {Observable<User>}
     */
    fetchCurrentUser() {
        // Create an error handler: current user is not found in Phonebook
        const handleCurrentUserError = (claims) => catchError((error) => {
            if (error instanceof HttpErrorResponse && error.status === 404) {
                if (claims.act?.email?.endsWith('@purestorage.com')) {
                    this.window.alert('You are impersonating a user who may not be allowed to log in to Pure1.\n' +
                        'Please reach out to #ask-cx-dtt for assistance addressing any issues in SFDC impacting this user.\n\n' +
                        'You will be redirected to your own account.');
                    // Initiate a login with auth-portal to get normal user token back
                    this.platformClient.login();
                }
                else {
                    // In very rare case, if a customer has been de-activated while being logged in.
                    this.platformClient.logout();
                }
            }
            // Re-throw error up the chain
            throw error;
        });
        // 1. Get token claims
        // 2. Get current user from Phonebook
        // Catch 404 error and handle it
        return this.platformClient.getIntrospect().pipe(switchMap((claims) => {
            const currentUser$ = this.http.get('/platform/current-user');
            return currentUser$.pipe(handleCurrentUserError(claims));
        }));
    }
    startFetching() {
        smartTimer(0, INTERVAL, INTERVAL)
            .pipe(takeUntil(this.destroy$), switchMap(() => this.fetchCurrentUser()))
            .subscribe(this.currentUser$);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: CachedCurrentUserService, deps: [{ token: i1.HttpClient }, { token: PlatformClientService }, { token: WINDOW }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: CachedCurrentUserService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: CachedCurrentUserService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.HttpClient }, { type: PlatformClientService }, { type: Window, decorators: [{
                    type: Inject,
                    args: [WINDOW]
                }] }] });

class FeatureFlagService {
    constructor() {
        this.featureFlagSubscriber$ = new Subject();
    }
    /**
     * Subscribe to get notified about all fetched feature flags
     * @returns Observable<FeatureFlagStatusResponse>
     */
    getFeatureFlagSubscriber() {
        return this.featureFlagSubscriber$.asObservable();
    }
    getFeatureFlags(names) {
        return from(platform().featureFlag.get(names)).pipe(tap((flags) => this.featureFlagSubscriber$.next(flags)));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FeatureFlagService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FeatureFlagService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FeatureFlagService, decorators: [{
            type: Injectable
        }] });

class FeatureFlagDirective {
    constructor(featureFlagService, templateRef, viewContainer) {
        this.featureFlagService = featureFlagService;
        this.templateRef = templateRef;
        this.viewContainer = viewContainer;
        this.destroyRef = inject(DestroyRef);
    }
    ngOnInit() {
        this.checkFeatureFlag();
    }
    checkFeatureFlag() {
        this.featureFlagService
            .getFeatureFlags(this.featureName)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((status) => {
            if (status[this.featureName].enabled) {
                this.viewContainer.createEmbeddedView(this.templateRef);
            }
            else {
                this.viewContainer.clear();
            }
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FeatureFlagDirective, deps: [{ token: FeatureFlagService }, { token: i0.TemplateRef }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.3.12", type: FeatureFlagDirective, isStandalone: true, selector: "[ifFeatureEnabled]", inputs: { featureName: ["ifFeatureEnabled", "featureName"] }, ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FeatureFlagDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[ifFeatureEnabled]',
                    standalone: true,
                }]
        }], ctorParameters: () => [{ type: FeatureFlagService }, { type: i0.TemplateRef }, { type: i0.ViewContainerRef }], propDecorators: { featureName: [{
                type: Input,
                args: ['ifFeatureEnabled']
            }] } });

class PlatformNotInitializedError extends Error {
    constructor() {
        super('ERR001: Pure1UIPlatform not found. Please make sure that the init.js script is injected correctly.');
    }
}
class ImpersonationDisabledError extends Error {
    constructor() {
        super("ERR002: Impersonation is disabled. Make sure to enable it in the platform's configuration (init.js).");
    }
}
class OrgSwitcherDataLoadingError extends Error {
    constructor(message = '') {
        super(`ERR003: Unexpected error when loading org switcher data. ${message}`);
    }
}
class OrgSwitcerDisabledError extends Error {
    constructor() {
        super("ERR004: 'org-switching' feature flag is disabled.");
    }
}

const provideImpersonationService = () => ({
    provide: ImpersonationService,
    useFactory: () => {
        if (!platform().impersonation) {
            throw new ImpersonationDisabledError();
        }
        return new ImpersonationService();
    },
});
class ImpersonationService {
    impersonate(email) {
        platform().impersonation.switchImpersonatedUser(email);
    }
    getImpersonationHistory() {
        return platform().impersonation.impersonateHistory();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ImpersonationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ImpersonationService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: ImpersonationService, decorators: [{
            type: Injectable
        }] });

class PhonebookV2Service {
    static { this.BASE_URL = '/api/phonebook/v2'; }
    constructor(http) {
        this.http = http;
    }
    getOrgSwitcherItems() {
        return this.http
            .get(PhonebookV2Service.BASE_URL + '/org-switcher/organizations')
            .pipe(map((response) => response.organizations));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PhonebookV2Service, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PhonebookV2Service }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: PhonebookV2Service, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.HttpClient }] });

/**
 * Service to manage organization switching functionality.
 *
 * **Important: this service assumes that the 'org-switching' feature flag is ON. It shall not be used otherwise.**
 *
 * This service provides methods to load and switch between organizations
 * that the current user can access. It also exposes observables to track
 * the active organization, user organizations, loading state, and loading errors.
 *
 * @see {@link PlatformClientService.getEffectiveOrg} if you are looking for the information about the effective org.
 *
 * @example
 * ```typescript
 * constructor(private orgSwitcherService: OrgSwitcherService) {}
 *
 * this.orgSwitcherService.loadOrgSwitcherItems();
 * this.orgSwitcherService.switch(123);
 *
 * this.orgSwitcherService.activeOrg$.subscribe(activeOrg => {
 *   console.log('Active Organization:', activeOrg);
 * });
 * ```
 */
class OrgSwitcherService {
    get _enabledOrThrow$() {
        return this.enabled$.pipe(map((enabled) => {
            if (!enabled) {
                throw new OrgSwitcerDisabledError();
            }
        }));
    }
    constructor(phonebookService, platform, amplitudeService, featureFlagService) {
        this.phonebookService = phonebookService;
        this.platform = platform;
        this.amplitudeService = amplitudeService;
        this.featureFlagService = featureFlagService;
        this._userOrgs$ = new ReplaySubject(1);
        this._isLoading$ = new BehaviorSubject(false);
        this._hasLoadingError$ = new BehaviorSubject(false);
    }
    /**
     * Initiates the loading of organizations that the current user can switch to.
     * @throws If the 'org-switching' feature flag is disabled
     */
    loadOrgSwitcherItems() {
        const isLoading = this._isLoading$.getValue();
        if (isLoading) {
            return;
        }
        this._enabledOrThrow$
            .pipe(tap(() => {
            this._hasLoadingError$.next(false);
            this._isLoading$.next(true);
        }), switchMap(() => this.phonebookService.getOrgSwitcherItems()), finalize(() => this._isLoading$.next(false)))
            .subscribe({
            next: (orgs) => this._userOrgs$.next(orgs),
            error: (error) => {
                this._hasLoadingError$.next(true);
                console.error(new OrgSwitcherDataLoadingError(error.message));
            },
        });
    }
    /**
     * Switches the active organization.
     *
     * The org switching is done by redirecting the user to the Auth Portal.
     *
     * @param orgId - The ID of the organization to switch to.
     * @throws If the 'org-switching' feature flag is disabled
     */
    async switch(orgId) {
        await firstValueFrom(this._enabledOrThrow$);
        const introspect = await firstValueFrom(this.platform.getIntrospect());
        const params = {
            phOid: String(orgId),
        };
        if (introspect.act) {
            params.impersonateEmail = introspect.email;
        }
        this.amplitudeService.tryTrackEvent(new OrganisationToSwitchSelected({
            targetOrgId: orgId,
            currentOrgId: introspect.ph_oid,
        }));
        this.platform.login(params);
    }
    /**
     * @returns An observable that emits the enabled status of the 'org-switching' feature flag.
     *
     * Use before trying to call any methods of this service.
     */
    get enabled$() {
        return this.featureFlagService.getFeatureFlags('org-switching').pipe(take(1), map((ffStatus) => ffStatus['org-switching'].enabled), shareReplay(1));
    }
    ;
    /**
     * @returns An observable of the organizations that the current user can switch to.
     * @throws If the 'org-switching' feature flag is disabled
     */
    get userOrgs$() {
        return this._enabledOrThrow$.pipe(switchMap(() => this._userOrgs$.asObservable()));
    }
    /**
     * Keeps track of the selected org in the org switcher
     * @returns An observable of the selected org item in the org switcher.
     * @throws If the 'org-switching' feature flag is disabled
     * @private
     */
    get activeOrg$() {
        return this._enabledOrThrow$.pipe(switchMap(() => this.userOrgs$), withLatestFrom(this.platform.getEffectiveOrg()), map(([orgs, effectiveOrg]) => orgs.find((o) => o.uid === effectiveOrg.uid)));
    }
    /**
     * @returns An observable of the loading error state
     * (`true` if an error occurred during the loading process, `false` otherwise).
     * @private
     */
    get hasLoadingError$() {
        return this._hasLoadingError$.asObservable().pipe(distinctUntilChanged());
    }
    /**
     * @returns An observable of the loading state
     * (`true` if the data is being loaded, `false` otherwise).
     * @private
     */
    get isLoading$() {
        return this._isLoading$.asObservable().pipe(distinctUntilChanged());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: OrgSwitcherService, deps: [{ token: PhonebookV2Service }, { token: PlatformClientService }, { token: AmplitudeService }, { token: FeatureFlagService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: OrgSwitcherService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: OrgSwitcherService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: PhonebookV2Service }, { type: PlatformClientService }, { type: AmplitudeService }, { type: FeatureFlagService }] });

/**
 * Organization switcher lists all organizations that the user can switch to.
 * Clicking on an organization will redirect the user to Auth Portal to switch their
 * access token to the selected organization.
 *
 * <b>Important</b>: Make sure that Kong allows your application to call Phonebook's <code>GET /v2/org-switcher/organizations</code> API.
 */
class OrgSwitcherComponent {
    constructor() {
        this.orgSwitcherService = inject(OrgSwitcherService);
        this.amplitudeService = inject(AmplitudeService);
        this.currentUserService = inject(CachedCurrentUserService);
        this.disabled = false;
        this.filterDebounceWait = 0;
        this.totalOrgsCount$ = this.orgSwitcherService.userOrgs$.pipe(map((orgs) => orgs.length));
        this.activeOrg$ = this.orgSwitcherService.activeOrg$;
        this.isLoading$ = this.orgSwitcherService.isLoading$;
        this.hasLoadingError$ = this.orgSwitcherService.hasLoadingError$;
        this.filter$ = new BehaviorSubject('');
        this.filteredOrgs$ = combineLatest([
            this.orgSwitcherService.userOrgs$,
            this.filter$.pipe(map((filter) => filter.trim().toLowerCase())),
        ]).pipe(map(([orgs, filter]) => orgs.filter((org) => org.name.toLowerCase().includes(filter))));
        this.groups$ = combineLatest([this.filteredOrgs$, this.currentUserService.getCurrentUser()]).pipe(map(([orgs, currentUser]) => {
            const groups = [
                {
                    name: 'My Organization',
                    items: orgs.filter((org) => org.opCompany.uid === currentUser.operationalCompany?.uid),
                },
                {
                    name: 'External Organizations',
                    items: orgs.filter((org) => org.opCompany.uid !== currentUser.operationalCompany?.uid),
                },
            ].filter((group) => group.items.length > 0);
            return groups;
        }));
        this.orgLabel$ = this.activeOrg$.pipe(map((org) => org.name));
        this.companyLabel$ = this.activeOrg$.pipe(map((org) => {
            if (org.opCompany?.name !== org.name) {
                return org.opCompany?.name;
            }
            else {
                return undefined;
            }
        }));
        this.hasMoreThanOneOrg$ = this.totalOrgsCount$.pipe(map((count) => count > 1));
    }
    ngOnInit() {
        this.loadData();
    }
    loadData() {
        this.orgSwitcherService.loadOrgSwitcherItems();
    }
    onFilterChange(term) {
        this.filter$.next(term.toLowerCase().trim());
    }
    onSelectionChange(item) {
        this.orgSwitcherService.switch(item.id);
    }
    async onVisibilityChange(visible) {
        if (!visible) {
            return;
        }
        const totalOrgsCount = await lastValueFrom(this.totalOrgsCount$.pipe(take(1)));
        this.amplitudeService.tryTrackEvent(new OrganisationSwitcherExpanded({ totalOrgsCount }));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: OrgSwitcherComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: OrgSwitcherComponent, isStandalone: true, selector: "platform-org-switcher", inputs: { disabled: "disabled" }, ngImport: i0, template: "<hive-organization-switcher\n    *ngIf=\"(hasMoreThanOneOrg$ | async) || (isLoading$ | async)\"\n    [selection]=\"activeOrg$ | async\"\n    [selectedCompanyLabel]=\"companyLabel$ | async\"\n    [selectedOrganizationLabel]=\"orgLabel$ | async\"\n    [isLoading]=\"isLoading$ | async\"\n    [hasFilterAutofocus]=\"true\"\n    [hasLoadingError]=\"hasLoadingError$ | async\"\n    [debounceWait]=\"filterDebounceWait\"\n    [disabled]=\"disabled\"\n    (selectionChange)=\"onSelectionChange($event)\"\n    (filterChange)=\"onFilterChange($event)\"\n    (tryAgainClick)=\"loadData()\"\n    (visibilityChange)=\"onVisibilityChange($event)\"\n>\n    <ng-container *ngFor=\"let section of groups$ | async\">\n        <hive-selection-menu-section [label]=\"section.name\">\n            <hive-selection-menu-item\n                *ngFor=\"let item of section.items\"\n                [label]=\"item.name\"\n                [value]=\"item\"\n            ></hive-selection-menu-item>\n        </hive-selection-menu-section>\n    </ng-container>\n</hive-organization-switcher>\n", styles: [":host{height:100%;display:inline-block}:host ::ng-deep .hive-organization-switcher-button-current-value label{display:inline}\n"], dependencies: [{ kind: "component", type: OrganizationSwitcherComponent, selector: "hive-organization-switcher", inputs: ["selectedOrganizationLabel", "selectedCompanyLabel", "debounceWait", "hasLoadingError", "isLoading", "disabled", "hasFilterAutofocus", "selection"], outputs: ["tryAgainClick", "selectionChange", "filterChange", "visibilityChange"] }, { kind: "ngmodule", type: SelectionMenuModule }, { kind: "component", type: i9.SelectionMenuItemComponent, selector: "hive-selection-menu-item", inputs: ["label", "value", "icon", "testId", "disabled"] }, { kind: "component", type: i9.SelectionMenuSectionComponent, selector: "hive-selection-menu-section", inputs: ["label"] }, { kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i2.AsyncPipe, name: "async" }, { kind: "ngmodule", type: TooltipModule }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: OrgSwitcherComponent, decorators: [{
            type: Component,
            args: [{ selector: 'platform-org-switcher', standalone: true, imports: [OrganizationSwitcherComponent, SelectionMenuModule, CommonModule, TooltipModule], changeDetection: ChangeDetectionStrategy.OnPush, template: "<hive-organization-switcher\n    *ngIf=\"(hasMoreThanOneOrg$ | async) || (isLoading$ | async)\"\n    [selection]=\"activeOrg$ | async\"\n    [selectedCompanyLabel]=\"companyLabel$ | async\"\n    [selectedOrganizationLabel]=\"orgLabel$ | async\"\n    [isLoading]=\"isLoading$ | async\"\n    [hasFilterAutofocus]=\"true\"\n    [hasLoadingError]=\"hasLoadingError$ | async\"\n    [debounceWait]=\"filterDebounceWait\"\n    [disabled]=\"disabled\"\n    (selectionChange)=\"onSelectionChange($event)\"\n    (filterChange)=\"onFilterChange($event)\"\n    (tryAgainClick)=\"loadData()\"\n    (visibilityChange)=\"onVisibilityChange($event)\"\n>\n    <ng-container *ngFor=\"let section of groups$ | async\">\n        <hive-selection-menu-section [label]=\"section.name\">\n            <hive-selection-menu-item\n                *ngFor=\"let item of section.items\"\n                [label]=\"item.name\"\n                [value]=\"item\"\n            ></hive-selection-menu-item>\n        </hive-selection-menu-section>\n    </ng-container>\n</hive-organization-switcher>\n", styles: [":host{height:100%;display:inline-block}:host ::ng-deep .hive-organization-switcher-button-current-value label{display:inline}\n"] }]
        }], propDecorators: { disabled: [{
                type: Input
            }] } });

class RoleService {
}

class SpogRoleService {
    static { this.BASE_URL = '/rest/v1/user'; }
    constructor(http) {
        this.http = http;
    }
    getCurrentUserRoles() {
        return this.http.get(SpogRoleService.BASE_URL).pipe(map((user) => [
            {
                name: user.role,
                alias: user.roleAlias,
                domain: 'PURE1',
            },
            ...user.additionalRoles.map((r) => ({
                name: r.name,
                alias: r.alias,
                domain: r.domain,
                status: r.status,
            })),
        ]));
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SpogRoleService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SpogRoleService }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SpogRoleService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: i1.HttpClient }] });

class Pure1UIPlatformModule {
    static forRoot(config) {
        if (!platform()) {
            throw new PlatformNotInitializedError();
        }
        const fallthroughAmplitudeTrackCallback = () => { };
        return {
            ngModule: Pure1UIPlatformModule,
            providers: [
                { provide: WINDOW, useFactory: windowFactory },
                PlatformClientService,
                CachedCurrentUserService,
                Pure1NavigationService,
                ApplicationsService,
                CachedApplicationsService,
                FeatureFlagService,
                AmplitudeService,
                provideImpersonationService(),
                PhonebookV2Service,
                OrgSwitcherService,
                SpogRoleService,
                {
                    provide: AMPLITUDE_TRACK_CALLBACK,
                    useValue: config?.amplitudeTrackCallback || fallthroughAmplitudeTrackCallback,
                },
                {
                    provide: RoleService,
                    useExisting: config?.roleService?.useExisting || SpogRoleService,
                },
            ],
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: Pure1UIPlatformModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.3.12", ngImport: i0, type: Pure1UIPlatformModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: Pure1UIPlatformModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: Pure1UIPlatformModule, decorators: [{
            type: NgModule
        }] });

class SwitchUserComponent {
    constructor(platformClientService, impersonationService, changeDetectionRef) {
        this.platformClientService = platformClientService;
        this.impersonationService = impersonationService;
        this.changeDetectionRef = changeDetectionRef;
        this.openChange = new EventEmitter();
        this.loading = true;
        this.items = [];
        this.impersonationHistory = [];
        this.currentFilter = '';
        this.impersonationForm = new FormGroup({
            email: new FormControl('', {
                validators: [Validators.required, Validators.email],
                nonNullable: true,
            }),
        });
        this.stopLoading$ = new Subject();
    }
    ngOnChanges() {
        this.dialogComponent?.toggle(this.open ? 'open' : 'closed');
        if (this.open) {
            this.initForm();
        }
    }
    ngOnDestroy() {
        this.stopLoading$.next();
        this.stopLoading$.complete();
    }
    initForm() {
        this.impersonationForm.reset();
        if (!this.loading) {
            return;
        }
        this.stopLoading$.next();
        forkJoin([
            this.platformClientService.getIntrospect().pipe(take(1)),
            this.impersonationService.getImpersonationHistory(),
        ])
            .pipe(takeUntil(this.stopLoading$))
            .subscribe(([introspect, history]) => {
            this.loading = false;
            this.impersonationHistory = history;
            this.introspect = introspect;
            this.refreshItems();
        });
    }
    refreshItems() {
        const filteredEmail = this.currentFilter.length > 0 ? this.currentFilter : null;
        this.items = [
            // If there is any filter display it as a first option
            ...(filteredEmail ? [filteredEmail] : []),
            // If user is impersonating, show the original user's email at the top
            // as a way to cancel impersonation
            ...(!filteredEmail && this.introspect?.act ? [this.introspect.act.email] : []),
            ...this.impersonationHistory.filter((email) => !filteredEmail || (email !== filteredEmail && email.includes(filteredEmail))),
        ];
        this.changeDetectionRef.detectChanges();
    }
    onFilterChange(newFilter) {
        this.currentFilter = newFilter.trim().toLowerCase();
        this.refreshItems();
    }
    onImpersonate() {
        this.impersonationService.impersonate(this.impersonationForm.controls.email.value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SwitchUserComponent, deps: [{ token: PlatformClientService }, { token: ImpersonationService }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.3.12", type: SwitchUserComponent, isStandalone: true, selector: "switch-user", inputs: { open: "open" }, outputs: { openChange: "openChange" }, viewQueries: [{ propertyName: "dialogComponent", first: true, predicate: ["dialogComponent"], descendants: true, static: true }], usesOnChanges: true, ngImport: i0, template: "<hive-content-dialog\n    #dialogComponent\n    (contentDialogClose)=\"openChange.emit(false)\"\n    closeOnBackdropClick\n    showCloseButton\n    heading=\"Impersonate User\"\n    size=\"small\"\n>\n    <form [formGroup]=\"impersonationForm\">\n        <hive-dropdown\n            [formControl]=\"impersonationForm.controls.email\"\n            label=\"Enter user email\"\n            [required]=\"true\"\n            [isLoading]=\"loading\"\n            [hasFilter]=\"true\"\n            (filterChange)=\"onFilterChange($event)\"\n            hiveAutofocus\n        >\n            <hive-selection-menu-item\n                *ngFor=\"let item of items\"\n                [label]=\"item\"\n                [value]=\"item\"\n            ></hive-selection-menu-item>\n        </hive-dropdown>\n    </form>\n\n    <hive-button\n        (click)=\"dialogComponent.hide()\"\n        label=\"Cancel\"\n        type=\"button\"\n        emphasis=\"secondary\"\n        slot=\"actions-right\"\n    ></hive-button>\n    <hive-button\n        [disabled]=\"!impersonationForm.valid\"\n        (click)=\"onImpersonate()\"\n        label=\"Impersonate\"\n        emphasis=\"primary\"\n        type=\"submit\"\n        slot=\"actions-right\"\n    ></hive-button>\n</hive-content-dialog>\n", dependencies: [{ kind: "component", type: ContentDialogComponent, selector: "hive-content-dialog", inputs: ["heading", "description", "headerInfoMessage", "size", "isLoading", "showCloseButton", "noPadding", "closeOnBackdropClick"], outputs: ["contentDialogClose"] }, { kind: "component", type: ButtonComponent, selector: "hive-button", inputs: ["type", "menuButtonActive", "hasMenuButtonCaret", "label", "accessibleName", "icon", "iconPosition", "emphasis", "title", "size", "isLoading", "disabled", "rounded", "width"] }, { kind: "ngmodule", type: DropdownModule }, { kind: "component", type: i9.DropdownComponent, selector: "hive-dropdown", inputs: ["selection", "size", "hasFilter", "debounceWait", "hasLoadingError", "isLoading", "disabled", "label", "required", "helpInfoIcon", "helpInfoMessage", "errorMessages", "assistanceText", "placeholder", "hasFilterAutofocus"], outputs: ["tryAgainClick", "selectionChange", "filterChange"] }, { kind: "ngmodule", type: ReactiveFormsModule }, { kind: "directive", type: i4.ɵNgNoValidate, selector: "form:not([ngNoForm]):not([ngNativeValidate])" }, { kind: "directive", type: i4.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i4.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i4.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i4.FormControlDirective, selector: "[formControl]", inputs: ["formControl", "disabled", "ngModel"], outputs: ["ngModelChange"], exportAs: ["ngForm"] }, { kind: "directive", type: i4.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "ngmodule", type: SelectionMenuModule }, { kind: "component", type: i9.SelectionMenuItemComponent, selector: "hive-selection-menu-item", inputs: ["label", "value", "icon", "testId", "disabled"] }, { kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: AutofocusDirective, selector: "[hiveAutofocus]", inputs: ["hiveAutofocus"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: SwitchUserComponent, decorators: [{
            type: Component,
            args: [{ selector: 'switch-user', standalone: true, imports: [
                        ContentDialogComponent,
                        ButtonComponent,
                        DropdownModule,
                        ReactiveFormsModule,
                        SelectionMenuModule,
                        CommonModule,
                        AutofocusDirective,
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<hive-content-dialog\n    #dialogComponent\n    (contentDialogClose)=\"openChange.emit(false)\"\n    closeOnBackdropClick\n    showCloseButton\n    heading=\"Impersonate User\"\n    size=\"small\"\n>\n    <form [formGroup]=\"impersonationForm\">\n        <hive-dropdown\n            [formControl]=\"impersonationForm.controls.email\"\n            label=\"Enter user email\"\n            [required]=\"true\"\n            [isLoading]=\"loading\"\n            [hasFilter]=\"true\"\n            (filterChange)=\"onFilterChange($event)\"\n            hiveAutofocus\n        >\n            <hive-selection-menu-item\n                *ngFor=\"let item of items\"\n                [label]=\"item\"\n                [value]=\"item\"\n            ></hive-selection-menu-item>\n        </hive-dropdown>\n    </form>\n\n    <hive-button\n        (click)=\"dialogComponent.hide()\"\n        label=\"Cancel\"\n        type=\"button\"\n        emphasis=\"secondary\"\n        slot=\"actions-right\"\n    ></hive-button>\n    <hive-button\n        [disabled]=\"!impersonationForm.valid\"\n        (click)=\"onImpersonate()\"\n        label=\"Impersonate\"\n        emphasis=\"primary\"\n        type=\"submit\"\n        slot=\"actions-right\"\n    ></hive-button>\n</hive-content-dialog>\n" }]
        }], ctorParameters: () => [{ type: PlatformClientService }, { type: ImpersonationService }, { type: i0.ChangeDetectorRef }], propDecorators: { open: [{
                type: Input,
                args: [{ required: true }]
            }], openChange: [{
                type: Output
            }], dialogComponent: [{
                type: ViewChild,
                args: ['dialogComponent', { static: true }]
            }] } });

/**
 * This service fetches list of auth factors for current user from AuthPortal
 */
class FactorsService {
    constructor(http) {
        this.http = http;
    }
    getFactors() {
        return this.http.get('/rest/v2/oob/factors');
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FactorsService, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FactorsService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: FactorsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: () => [{ type: i1.HttpClient }] });

var UserProfileItem;
(function (UserProfileItem) {
    UserProfileItem["ManageMyProfile"] = "Manage My Profile Clicked";
    UserProfileItem["ImpersonateUser"] = "Impersonate User Clicked";
    UserProfileItem["SetUpMFA"] = "Set Up MFA Clicked";
    UserProfileItem["Logout"] = "Logout Clicked";
})(UserProfileItem || (UserProfileItem = {}));
class UserProfileButtonEvent extends GenericAmplitudeEvent {
    constructor(event_type, event_properties) {
        super(event_type, event_properties);
    }
}
var LoadingState;
(function (LoadingState) {
    LoadingState[LoadingState["None"] = 0] = "None";
    LoadingState[LoadingState["Pending"] = 1] = "Pending";
    LoadingState[LoadingState["Complete"] = 2] = "Complete";
    LoadingState[LoadingState["Error"] = 3] = "Error";
})(LoadingState || (LoadingState = {}));
class UserProfileButtonComponent {
    constructor(cachedCurrentUserService, platformClientService, applicationsService, factorsService, changeDetectionRef, authorizationService, amplitudeService, impersonationService, roleService) {
        this.cachedCurrentUserService = cachedCurrentUserService;
        this.platformClientService = platformClientService;
        this.applicationsService = applicationsService;
        this.factorsService = factorsService;
        this.changeDetectionRef = changeDetectionRef;
        this.authorizationService = authorizationService;
        this.amplitudeService = amplitudeService;
        this.impersonationService = impersonationService;
        this.roleService = roleService;
        this.destroy$ = new Subject();
        /** Invert the colors of the separator */
        this.inverted = true;
        this.isInIdentityCenter = false;
        this.userProfilePageUrl = '';
        this.userProfileAccountSecurityPageUrl = '';
        this.impersonated = false;
        this.impersonationAuthorized = false;
        this.hasAnyFactor = false;
        this.impersonationSupported = false;
        this.isImpersonationDialogOpen = false;
        this.canReadAuthDetails = false;
        this.userEmail = '';
        this.impersonateEmail = '';
        // Indicator of the loading state when the profile gets open
        // - available menu items
        this.detailedLoadingState = LoadingState.None;
        // Loading of the user info (happens on init) - to display user icon (1st letter)
        this.userLoadingState = LoadingState.Pending;
        this.LoadingState = LoadingState;
        this.UserProfileItem = UserProfileItem;
    }
    ngOnInit() {
        this.isInIdentityCenter = this.platformClientService.getApplicationId() === 'identity-center';
        this.impersonationSupported = this.platformClientService.impersonationSupported();
        this.cachedCurrentUserService
            .getCurrentUser()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
            next: (user) => {
                this.user = user;
                this.userLoadingState = LoadingState.Complete;
                this.changeDetectionRef.detectChanges();
            },
            error: () => {
                this.userLoadingState = LoadingState.Error;
            },
        });
        firstValueFrom(this.platformClientService.getIntrospect()).then((introspect) => {
            this.impersonated = Boolean(introspect.act);
            if (introspect.act) {
                this.userEmail = introspect.act.email;
                this.impersonateEmail = introspect.email;
            }
            else {
                this.userEmail = introspect.email;
            }
        });
        firstValueFrom(this.authorizationService.isAllowed('PURE1:read:switch_user')).then((isAllowed) => {
            this.impersonationAuthorized = isAllowed;
        });
        this.roleService
            .getCurrentUserRoles()
            .pipe(take(1))
            .subscribe({
            next: (roles) => {
                this.roles = roles;
                this.hiddenRoleSubset = roles.length > 2 ? roles.slice(1) : [];
                this.changeDetectionRef.detectChanges();
            },
        });
    }
    openDetail() {
        if (this.loading || this.detailedLoadingState === LoadingState.Complete) {
            return;
        }
        this.loadDetails();
    }
    // Configures user profile menu with available links
    // according to permissions and available apps
    loadDetails() {
        this.detailedLoadingState = LoadingState.Pending;
        forkJoin([
            this.applicationsService.getUserApps().pipe(take(1)),
            this.authorizationService.isAllowed('PURE1:read:authentication_details').pipe(take(1), tap((canReadAuthDetails) => (this.canReadAuthDetails = canReadAuthDetails)), switchMap((canReadAuthDetails) => canReadAuthDetails ? this.factorsService.getFactors().pipe(take(1)) : of([]))),
        ])
            .pipe(takeUntil(this.destroy$))
            .subscribe({
            next: ([apps, factors]) => {
                this.detailedLoadingState = LoadingState.Complete;
                this.identityCenterApp = apps.find((app) => app.id === 'identity-center');
                this.hasAnyFactor = factors.length > 0;
                this.changeDetectionRef.detectChanges();
            },
            error: () => {
                this.detailedLoadingState = LoadingState.Error;
            },
        });
    }
    tryTrackEvent(event) {
        this.amplitudeService.tryTrackEvent(new UserProfileButtonEvent(event));
    }
    logout() {
        this.amplitudeService.tryTrackEvent(new UserProfileButtonEvent(UserProfileItem.Logout));
        this.platformClientService.logout();
    }
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
    openImpersonationDialog() {
        this.isImpersonationDialogOpen = true;
    }
    exitImpersonation() {
        this.impersonationService.impersonate(this.userEmail);
    }
    get loading() {
        return this.userLoadingState === LoadingState.Pending || this.detailedLoadingState === LoadingState.Pending;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserProfileButtonComponent, deps: [{ token: CachedCurrentUserService }, { token: PlatformClientService }, { token: CachedApplicationsService }, { token: FactorsService }, { token: i0.ChangeDetectorRef }, { token: i5.AuthorizationService }, { token: AmplitudeService }, { token: ImpersonationService }, { token: RoleService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "17.3.12", type: UserProfileButtonComponent, isStandalone: true, selector: "user-profile-button", inputs: { inverted: "inverted" }, viewQueries: [{ propertyName: "avatarMenu", first: true, predicate: AvatarMenuComponent, descendants: true, static: true }], ngImport: i0, template: "<hive-avatar-menu (click)=\"openDetail()\" title=\"User Profile\">\n    <hive-avatar\n        [inverted]=\"inverted\"\n        [impersonated]=\"impersonated\"\n        [letter]=\"user?.firstName?.charAt(0)?.toLocaleUpperCase()\"\n        size=\"small\"\n    ></hive-avatar>\n    <ng-template hiveTemplate=\"menuContent\">\n        <div hive-root class=\"popup\">\n            <hive-loading-spinner *ngIf=\"loading; else popupContent\" displayMode=\"centered\" />\n        </div>\n    </ng-template>\n</hive-avatar-menu>\n\n<ng-template #popupContent>\n    <div class=\"container\">\n        <div class=\"avatar-content divider-bottom\">\n            <hive-avatar\n                [impersonated]=\"impersonated\"\n                [letter]=\"user?.firstName?.charAt(0)?.toLocaleUpperCase()\"\n                size=\"medium\"\n            ></hive-avatar>\n            <div class=\"user-detail\">\n                <div class=\"user-name-wrapper\">\n                    <h6 class=\"user-name hive-header-semibold\">{{ user?.firstName }} {{ user?.lastName }}</h6>\n                    <p class=\"user-email hive-paragraph-small hive-paragraph-regular\" [attr.title]=\"user?.email\">\n                        {{ user?.email }}\n                    </p>\n                </div>\n\n                @if (!roles) {\n                    <hive-loading-spinner displayMode=\"centered\" />\n                } @else {\n                    <ul id=\"list_roles\" class=\"list-roles\">\n                        @if (roles.length < 3) {\n                            <li class=\"role-item\" *ngFor=\"let role of roles\">\n                                <hive-tag [label]=\"role.alias\" />\n                            </li>\n                        } @else {\n                            <li class=\"role-item\">\n                                <hive-tag [label]=\"roles[0].alias\" />\n                            </li>\n                            <li>\n                                <hive-tag\n                                    tabindex=\"0\"\n                                    class=\"tag-actionable\"\n                                    role=\"button\"\n                                    aria-haspopup=\"true\"\n                                    [label]=\"'+' + (roles.length - 1) + ' scopes'\"\n                                    [hivePopUpTrigger]=\"popup\"\n                                />\n                                <hive-pop-up\n                                    closeOnOutsideClick\n                                    id=\"nested_list_roles\"\n                                    #popup\n                                    position=\"bottomEnd\"\n                                    [offsetY]=\"8\"\n                                >\n                                    <ul class=\"list-roles-popup\">\n                                        <li class=\"role-item\" *ngFor=\"let role of hiddenRoleSubset\">\n                                            {{ role.alias }}\n                                        </li>\n                                    </ul>\n                                </hive-pop-up>\n                            </li>\n                        }\n                    </ul>\n                }\n                <hive-label\n                    leftIcon=\"light/building.svg\"\n                    [label]=\"user?.operationalCompany?.name\"\n                    size=\"smallest\"\n                    weight=\"semibold\"\n                ></hive-label>\n\n                <hive-tag *ngIf=\"impersonated\" label=\"You are impersonating\" severity=\"major\"></hive-tag>\n            </div>\n        </div>\n        <div class=\"menu-items\">\n            <div class=\"group divider-bottom\">\n                <a\n                    *ngIf=\"isInIdentityCenter\"\n                    (click)=\"tryTrackEvent(UserProfileItem.ManageMyProfile); avatarMenu.hideMenu()\"\n                    [routerLink]=\"['/profile']\"\n                    class=\"item\"\n                >\n                    <hive-label leftIcon=\"light/user.svg\" size=\"small\" label=\"Manage My Profile\"></hive-label>\n                </a>\n                <a\n                    *ngIf=\"!isInIdentityCenter\"\n                    (click)=\"tryTrackEvent(UserProfileItem.ManageMyProfile)\"\n                    [attr.href]=\"'identity-center' | pure1AppLink: 'app/profile'\"\n                    target=\"_blank\"\n                    class=\"item\"\n                >\n                    <hive-icon-component icon=\"light/user.svg\" size=\"small\" />\n                    <span>Manage My Profile</span>\n                    <hive-icon-component\n                        class=\"external-link-icon\"\n                        icon=\"light/arrow-up-right-from-square.svg\"\n                        size=\"small\"\n                    />\n                </a>\n                <a\n                    *ngIf=\"impersonationSupported && impersonationAuthorized\"\n                    (click)=\"tryTrackEvent(UserProfileItem.ImpersonateUser); openImpersonationDialog()\"\n                    role=\"button\"\n                    class=\"item\"\n                    data-testid=\"impersonate_btn\"\n                >\n                    <hive-icon-component icon=\"light/user-group.svg\" size=\"small\" />\n                    <span>Impersonate User</span>\n                </a>\n                <a *ngIf=\"impersonated\" (click)=\"exitImpersonation()\" role=\"button\" class=\"item\">\n                    <hive-icon-component icon=\"light/octagon-xmark.svg\" size=\"small\" />\n                    <span>Exit Impersonation</span>\n                </a>\n            </div>\n            <div *ngIf=\"canReadAuthDetails && !hasAnyFactor\" class=\"group divider-bottom\">\n                <a\n                    *ngIf=\"isInIdentityCenter\"\n                    (click)=\"tryTrackEvent(UserProfileItem.SetUpMFA); avatarMenu.hideMenu()\"\n                    [routerLink]=\"['/profile/account-security']\"\n                    class=\"item\"\n                >\n                    <hive-icon-component icon=\"light/mobile.svg\" size=\"small\" />\n                    <span>Set Up Multi-Factor Authentication</span>\n                </a>\n                <a\n                    *ngIf=\"!isInIdentityCenter\"\n                    (click)=\"tryTrackEvent(UserProfileItem.SetUpMFA)\"\n                    [attr.href]=\"'identity-center' | pure1AppLink: 'app/profile/account-security'\"\n                    target=\"_blank\"\n                    class=\"item\"\n                >\n                    <hive-icon-component icon=\"light/mobile.svg\" size=\"small\" />\n                    <span>Set Up Multi-Factor Authentication</span>\n                    <hive-icon-component\n                        class=\"external-link-icon\"\n                        icon=\"light/arrow-up-right-from-square.svg\"\n                        size=\"small\"\n                    />\n                </a>\n            </div>\n            <div class=\"group\">\n                <a (click)=\"logout()\" class=\"item link-danger\">\n                    <hive-icon-component icon=\"light/arrow-right-from-bracket.svg\" size=\"small\" />\n                    <span>Logout</span>\n                </a>\n            </div>\n        </div>\n    </div>\n    <switch-user [(open)]=\"isImpersonationDialogOpen\"></switch-user>\n</ng-template>\n", styles: [".popup{width:288px;min-height:300px;display:flex;flex-direction:column;align-items:center;justify-content:center}.popup .container{display:flex;flex-direction:column}.popup .divider-bottom{border-width:0 0 1px 0;border-style:solid;border-color:#ede9eb}.popup .avatar-content{display:flex;flex-direction:column;align-items:center;padding:16px}.popup .user-detail{padding-top:16px;display:flex;flex-direction:column;align-items:center;gap:8px;width:100%;overflow:hidden}.popup .user-name-wrapper{display:flex;flex-direction:column;align-items:center;gap:2px;width:100%;overflow:hidden}.popup .user-name{margin:0}.popup .user-email{width:100%;overflow:hidden;text-overflow:ellipsis;text-align:center}.popup .menu-items .group{padding-top:16px;padding-bottom:16px;display:flex;flex-direction:column}.popup .menu-items .group:last-child{padding-bottom:0}.popup .menu-items .item{display:inline-flex;align-items:center;text-decoration:none;padding:8px;color:#5a4f54;gap:4px;line-height:16px}.popup .menu-items .item:hover{background-color:#f8f8f8;cursor:pointer}.link-danger{color:#db1616!important}.link-danger ::ng-deep hive-icon-component svg{fill:#db1616}.external-link-icon{display:inline-block;margin-left:auto}.list-roles{list-style-type:none;margin:0;padding:0;display:flex;gap:8px}.list-roles-popup{list-style-type:none;margin:0;padding:0;color:#ada6a9}.tag-actionable{cursor:pointer}.tag-actionable:hover{outline:1px solid #982862}.spinner-container{display:flex;justify-content:center;align-items:center;height:100%}\n"], dependencies: [{ kind: "ngmodule", type: AvatarModule }, { kind: "component", type: i9.AvatarComponent, selector: "hive-avatar", inputs: ["letter", "size", "impersonated"] }, { kind: "component", type: i9.AvatarMenuComponent, selector: "hive-avatar-menu", inputs: ["expanded", "userName", "userRole", "userRoleSubtext"] }, { kind: "component", type: IconComponent, selector: "hive-icon-component", inputs: ["icon", "size"] }, { kind: "component", type: LoadingSpinnerComponent, selector: "hive-loading-spinner", inputs: ["displayMode", "label", "size"] }, { kind: "directive", type: RootDirective, selector: "[hive-root]" }, { kind: "component", type: LabelComponent, selector: "hive-label", inputs: ["label", "for", "size", "weight", "maxWeight", "horizontalAlignment", "leftIconHelpInfoMessage", "rightIconHelpInfoMessage", "leftIcon", "rightIcon", "required", "textOverflowEllipsis"] }, { kind: "ngmodule", type: RouterModule }, { kind: "directive", type: i10.RouterLink, selector: "[routerLink]", inputs: ["target", "queryParams", "fragment", "queryParamsHandling", "state", "info", "relativeTo", "preserveFragment", "skipLocationChange", "replaceUrl", "routerLink"] }, { kind: "component", type: SwitchUserComponent, selector: "switch-user", inputs: ["open"], outputs: ["openChange"] }, { kind: "ngmodule", type: AuthorizerModule }, { kind: "component", type: TagComponent, selector: "hive-tag", inputs: ["severity", "label", "size"] }, { kind: "ngmodule", type: PopUpModule }, { kind: "component", type: i9.PopUpComponent, selector: "hive-pop-up", inputs: ["testId", "showCloseButton", "dark", "position", "offsetX", "offsetY", "closeOnOutsideClick", "closeOnBackdropClick", "hasBackdrop"], outputs: ["visibilityChange"], exportAs: ["hivePopUp"] }, { kind: "directive", type: i9.PopUpTriggerDirective, selector: "[hivePopUpTrigger]", inputs: ["hivePopUpTrigger"] }, { kind: "pipe", type: Pure1AppLinkPipe, name: "pure1AppLink" }, { kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i2.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: HiveTemplateDirective, selector: "ng-template[hiveTemplate]", inputs: ["hiveTemplate"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.3.12", ngImport: i0, type: UserProfileButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'user-profile-button', standalone: true, imports: [
                        AvatarModule,
                        IconComponent,
                        LoadingSpinnerComponent,
                        RootDirective,
                        LabelComponent,
                        RouterModule,
                        SwitchUserComponent,
                        AuthorizerModule,
                        TagComponent,
                        PopUpModule,
                        Pure1AppLinkPipe,
                        CommonModule,
                        HiveTemplateDirective,
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<hive-avatar-menu (click)=\"openDetail()\" title=\"User Profile\">\n    <hive-avatar\n        [inverted]=\"inverted\"\n        [impersonated]=\"impersonated\"\n        [letter]=\"user?.firstName?.charAt(0)?.toLocaleUpperCase()\"\n        size=\"small\"\n    ></hive-avatar>\n    <ng-template hiveTemplate=\"menuContent\">\n        <div hive-root class=\"popup\">\n            <hive-loading-spinner *ngIf=\"loading; else popupContent\" displayMode=\"centered\" />\n        </div>\n    </ng-template>\n</hive-avatar-menu>\n\n<ng-template #popupContent>\n    <div class=\"container\">\n        <div class=\"avatar-content divider-bottom\">\n            <hive-avatar\n                [impersonated]=\"impersonated\"\n                [letter]=\"user?.firstName?.charAt(0)?.toLocaleUpperCase()\"\n                size=\"medium\"\n            ></hive-avatar>\n            <div class=\"user-detail\">\n                <div class=\"user-name-wrapper\">\n                    <h6 class=\"user-name hive-header-semibold\">{{ user?.firstName }} {{ user?.lastName }}</h6>\n                    <p class=\"user-email hive-paragraph-small hive-paragraph-regular\" [attr.title]=\"user?.email\">\n                        {{ user?.email }}\n                    </p>\n                </div>\n\n                @if (!roles) {\n                    <hive-loading-spinner displayMode=\"centered\" />\n                } @else {\n                    <ul id=\"list_roles\" class=\"list-roles\">\n                        @if (roles.length < 3) {\n                            <li class=\"role-item\" *ngFor=\"let role of roles\">\n                                <hive-tag [label]=\"role.alias\" />\n                            </li>\n                        } @else {\n                            <li class=\"role-item\">\n                                <hive-tag [label]=\"roles[0].alias\" />\n                            </li>\n                            <li>\n                                <hive-tag\n                                    tabindex=\"0\"\n                                    class=\"tag-actionable\"\n                                    role=\"button\"\n                                    aria-haspopup=\"true\"\n                                    [label]=\"'+' + (roles.length - 1) + ' scopes'\"\n                                    [hivePopUpTrigger]=\"popup\"\n                                />\n                                <hive-pop-up\n                                    closeOnOutsideClick\n                                    id=\"nested_list_roles\"\n                                    #popup\n                                    position=\"bottomEnd\"\n                                    [offsetY]=\"8\"\n                                >\n                                    <ul class=\"list-roles-popup\">\n                                        <li class=\"role-item\" *ngFor=\"let role of hiddenRoleSubset\">\n                                            {{ role.alias }}\n                                        </li>\n                                    </ul>\n                                </hive-pop-up>\n                            </li>\n                        }\n                    </ul>\n                }\n                <hive-label\n                    leftIcon=\"light/building.svg\"\n                    [label]=\"user?.operationalCompany?.name\"\n                    size=\"smallest\"\n                    weight=\"semibold\"\n                ></hive-label>\n\n                <hive-tag *ngIf=\"impersonated\" label=\"You are impersonating\" severity=\"major\"></hive-tag>\n            </div>\n        </div>\n        <div class=\"menu-items\">\n            <div class=\"group divider-bottom\">\n                <a\n                    *ngIf=\"isInIdentityCenter\"\n                    (click)=\"tryTrackEvent(UserProfileItem.ManageMyProfile); avatarMenu.hideMenu()\"\n                    [routerLink]=\"['/profile']\"\n                    class=\"item\"\n                >\n                    <hive-label leftIcon=\"light/user.svg\" size=\"small\" label=\"Manage My Profile\"></hive-label>\n                </a>\n                <a\n                    *ngIf=\"!isInIdentityCenter\"\n                    (click)=\"tryTrackEvent(UserProfileItem.ManageMyProfile)\"\n                    [attr.href]=\"'identity-center' | pure1AppLink: 'app/profile'\"\n                    target=\"_blank\"\n                    class=\"item\"\n                >\n                    <hive-icon-component icon=\"light/user.svg\" size=\"small\" />\n                    <span>Manage My Profile</span>\n                    <hive-icon-component\n                        class=\"external-link-icon\"\n                        icon=\"light/arrow-up-right-from-square.svg\"\n                        size=\"small\"\n                    />\n                </a>\n                <a\n                    *ngIf=\"impersonationSupported && impersonationAuthorized\"\n                    (click)=\"tryTrackEvent(UserProfileItem.ImpersonateUser); openImpersonationDialog()\"\n                    role=\"button\"\n                    class=\"item\"\n                    data-testid=\"impersonate_btn\"\n                >\n                    <hive-icon-component icon=\"light/user-group.svg\" size=\"small\" />\n                    <span>Impersonate User</span>\n                </a>\n                <a *ngIf=\"impersonated\" (click)=\"exitImpersonation()\" role=\"button\" class=\"item\">\n                    <hive-icon-component icon=\"light/octagon-xmark.svg\" size=\"small\" />\n                    <span>Exit Impersonation</span>\n                </a>\n            </div>\n            <div *ngIf=\"canReadAuthDetails && !hasAnyFactor\" class=\"group divider-bottom\">\n                <a\n                    *ngIf=\"isInIdentityCenter\"\n                    (click)=\"tryTrackEvent(UserProfileItem.SetUpMFA); avatarMenu.hideMenu()\"\n                    [routerLink]=\"['/profile/account-security']\"\n                    class=\"item\"\n                >\n                    <hive-icon-component icon=\"light/mobile.svg\" size=\"small\" />\n                    <span>Set Up Multi-Factor Authentication</span>\n                </a>\n                <a\n                    *ngIf=\"!isInIdentityCenter\"\n                    (click)=\"tryTrackEvent(UserProfileItem.SetUpMFA)\"\n                    [attr.href]=\"'identity-center' | pure1AppLink: 'app/profile/account-security'\"\n                    target=\"_blank\"\n                    class=\"item\"\n                >\n                    <hive-icon-component icon=\"light/mobile.svg\" size=\"small\" />\n                    <span>Set Up Multi-Factor Authentication</span>\n                    <hive-icon-component\n                        class=\"external-link-icon\"\n                        icon=\"light/arrow-up-right-from-square.svg\"\n                        size=\"small\"\n                    />\n                </a>\n            </div>\n            <div class=\"group\">\n                <a (click)=\"logout()\" class=\"item link-danger\">\n                    <hive-icon-component icon=\"light/arrow-right-from-bracket.svg\" size=\"small\" />\n                    <span>Logout</span>\n                </a>\n            </div>\n        </div>\n    </div>\n    <switch-user [(open)]=\"isImpersonationDialogOpen\"></switch-user>\n</ng-template>\n", styles: [".popup{width:288px;min-height:300px;display:flex;flex-direction:column;align-items:center;justify-content:center}.popup .container{display:flex;flex-direction:column}.popup .divider-bottom{border-width:0 0 1px 0;border-style:solid;border-color:#ede9eb}.popup .avatar-content{display:flex;flex-direction:column;align-items:center;padding:16px}.popup .user-detail{padding-top:16px;display:flex;flex-direction:column;align-items:center;gap:8px;width:100%;overflow:hidden}.popup .user-name-wrapper{display:flex;flex-direction:column;align-items:center;gap:2px;width:100%;overflow:hidden}.popup .user-name{margin:0}.popup .user-email{width:100%;overflow:hidden;text-overflow:ellipsis;text-align:center}.popup .menu-items .group{padding-top:16px;padding-bottom:16px;display:flex;flex-direction:column}.popup .menu-items .group:last-child{padding-bottom:0}.popup .menu-items .item{display:inline-flex;align-items:center;text-decoration:none;padding:8px;color:#5a4f54;gap:4px;line-height:16px}.popup .menu-items .item:hover{background-color:#f8f8f8;cursor:pointer}.link-danger{color:#db1616!important}.link-danger ::ng-deep hive-icon-component svg{fill:#db1616}.external-link-icon{display:inline-block;margin-left:auto}.list-roles{list-style-type:none;margin:0;padding:0;display:flex;gap:8px}.list-roles-popup{list-style-type:none;margin:0;padding:0;color:#ada6a9}.tag-actionable{cursor:pointer}.tag-actionable:hover{outline:1px solid #982862}.spinner-container{display:flex;justify-content:center;align-items:center;height:100%}\n"] }]
        }], ctorParameters: () => [{ type: CachedCurrentUserService }, { type: PlatformClientService }, { type: CachedApplicationsService }, { type: FactorsService }, { type: i0.ChangeDetectorRef }, { type: i5.AuthorizationService }, { type: AmplitudeService }, { type: ImpersonationService }, { type: RoleService }], propDecorators: { inverted: [{
                type: Input
            }], avatarMenu: [{
                type: ViewChild,
                args: [AvatarMenuComponent, { static: true }]
            }] } });

/*
 * Public API Surface of angular-client
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AppSwitcherComponent, AppSwitcherContentsComponent, ApplicationsService, CachedCurrentUserService, FeatureFlagDirective, FeatureFlagService, ImpersonationService, OrgSwitcherComponent, OrgSwitcherService, PlatformClientService, Pure1AppLinkPipe, Pure1NavigationService, Pure1UIPlatformModule, RoleService, UserProfileButtonComponent, provideImpersonationService };
